/**
 * Where a model's two date windows sit relative to each other.
 *
 * The training and validation windows were two cells in a table, which can say when each
 * one runs and can never say how they sit together: whether validation picks up where
 * training stops, whether there is a gap between them, whether they overlap. That is the
 * one thing about these two datasets that is genuinely spatial, and the only thing on
 * this card a picture does better than a number.
 *
 * Geometry only. Nothing here judges what it computes: whether two windows should
 * overlap is a modelling question this module has no business answering, so an overlap
 * is placed accurately and left to be read.
 */

export interface DateWindow {
  start: string;
  end: string;
}

export interface TimelineBand {
  /** Percent of the axis from its left edge. */
  leftPct: number;
  /** Percent of the axis wide. */
  widthPct: number;
  /** "Sep 2024 - Aug 2025". */
  label: string;
  /** Whole months, at least 1, the way the old Timeframe row counted them. */
  months: number;
}

export interface DatasetTimeline {
  training: TimelineBand;
  validation: TimelineBand;
  /** The axis ends, as labels. */
  from: string;
  to: string;
}

const MONTH_LABEL = { month: 'short', year: 'numeric' } as const;

// A band narrower than this is a line rather than a band: a one-week window inside a
// three-year axis rounds to a fraction of a percent and disappears. Widening it distorts
// the geometry by under half a percent of the axis, which is less wrong than drawing
// nothing where a dataset is.
const MIN_WIDTH_PCT = 1;

function parse(
  window?: DateWindow | null
): { from: number; to: number } | null {
  if (!window?.start || !window?.end) return null;
  const from = Date.parse(window.start);
  const to = Date.parse(window.end);
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return null;
  return { from, to };
}

function label(from: number, to: number): string {
  const one = new Date(from).toLocaleDateString('en-US', MONTH_LABEL);
  const other = new Date(to).toLocaleDateString('en-US', MONTH_LABEL);
  return one === other ? one : `${one} - ${other}`;
}

/**
 * Whole months in a span, at least one.
 *
 * An average-month approximation, where the Timeframe row this replaces uses moment's
 * calendar-aware `diff(months, true)`. Different arithmetic, and it agrees with that row
 * on every case checked, including month ends, a leap February and a five-year span. So
 * a window that read as "11 months" in the table does not become 10 or 12 the day it
 * becomes a band; the two are not the same function, so a case that disagrees is
 * possible rather than impossible.
 */
export function monthsBetween(from: number, to: number): number {
  const MS_PER_MONTH = (1000 * 60 * 60 * 24 * 365.25) / 12;
  return Math.max(1, Math.round((to - from) / MS_PER_MONTH));
}

/**
 * The two windows as bands on a shared axis, or null when they cannot be drawn.
 *
 * Null covers every case a picture would lie about: a missing date, an unparseable one,
 * a window that ends before it starts, and two windows that collapse to a single instant
 * so there is no axis to place them on. The caller falls back to the rows.
 */
export function buildTimeline(
  training?: DateWindow | null,
  validation?: DateWindow | null
): DatasetTimeline | null {
  const t = parse(training);
  const v = parse(validation);
  if (!t || !v) return null;

  const axisFrom = Math.min(t.from, v.from);
  const axisTo = Math.max(t.to, v.to);
  const span = axisTo - axisFrom;
  if (span <= 0) return null;

  const band = (window: { from: number; to: number }): TimelineBand => ({
    leftPct: ((window.from - axisFrom) / span) * 100,
    widthPct: Math.max(MIN_WIDTH_PCT, ((window.to - window.from) / span) * 100),
    label: label(window.from, window.to),
    months: monthsBetween(window.from, window.to),
  });

  return {
    training: band(t),
    validation: band(v),
    from: new Date(axisFrom).toLocaleDateString('en-US', MONTH_LABEL),
    to: new Date(axisTo).toLocaleDateString('en-US', MONTH_LABEL),
  };
}
